import React, { useEffect, useRef, useState } from "react";
import { api, formatError, resolvePhotoUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2 } from "@/lib/icons";
import { useSettings } from "@/lib/settings";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const MAX_MB = 12;
const CLIENT_TARGET_BYTES = 220 * 1024;

export async function preparePhoto(file) {
  let bitmap;
  let objectUrl;
  if (window.createImageBitmap) {
    bitmap = await window.createImageBitmap(file);
  } else {
    // Older Safari versions do not expose createImageBitmap. Decode through
    // an Image element so mobile registration still uploads a small request.
    objectUrl = URL.createObjectURL(file);
    bitmap = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("The selected photo could not be read"));
      image.src = objectUrl;
    });
  }
  try {
    const sourceWidth = bitmap.width || bitmap.naturalWidth;
    const sourceHeight = bitmap.height || bitmap.naturalHeight;
    const scale = Math.min(1, 900 / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { alpha: false });
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);

    let quality = 0.82;
    let blob;
    do {
      blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", quality));
      quality -= 0.1;
    } while (blob && blob.size > CLIENT_TARGET_BYTES && quality >= 0.42);

    if (!blob) return file;
    const baseName = (file.name || "profile").replace(/\.[^.]+$/, "");
    const type = blob.type || "image/jpeg";
    const extension = type === "image/webp" ? "webp" : "jpg";
    return new File([blob], `${baseName}.${extension}`, { type });
  } finally {
    bitmap.close?.();
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Reusable profile-photo uploader.
 * @param {string} src – current photo URL (raw, may be relative /api/photos/…)
 * @param {(newUrl: string|null) => void} onChange – called after successful upload/delete
 * @param {string} endpoint – upload endpoint (e.g. `/member/photo` or `/admin/members/{id}/photo`)
 * @param {string} deleteEndpoint – delete endpoint (same URL, DELETE)
 * @param {boolean} allowDelete – show trash button
 * @param {"sm"|"md"|"lg"} size
 * @param {string} testidPrefix
 * @param {string} name – member name used by the shared initials avatar
 */
export default function PhotoUpload({
  src, onChange, endpoint, deleteEndpoint, allowDelete = true, size = "md",
  testidPrefix = "photo", name = "", avatarKey = null,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const px = size === "sm" ? 80 : size === "lg" ? 160 : 120;

  const doUpload = async (f) => {
    if (!f) return;
    if (!ACCEPT.split(",").includes(f.type.toLowerCase())) {
      toast.error("Only JPG, PNG or WEBP files are allowed");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Photo must be ${MAX_MB} MB or less`);
      return;
    }
    setBusy(true);
    try {
      const prepared = await preparePhoto(f);
      const fd = new FormData();
      fd.append("file", prepared);
      const { data } = await api.post(endpoint, fd);
      if (!resolvePhotoUrl(data?.photo_url)) throw new Error("The server did not return the uploaded photo");
      toast.success("Photo updated");
      onChange?.(data.photo_url);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const doDelete = async () => {
    if (!window.confirm("Remove profile photo?")) return;
    setBusy(true);
    try {
      await api.delete(deleteEndpoint || endpoint);
      toast.success("Photo removed");
      onChange?.(null);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const resolved = resolvePhotoUrl(src);

  return (
    <div className="flex items-center gap-4">
      <div
        className="relative shrink-0 rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-violet-800/40 to-magenta-700/30"
        style={{ width: px, height: px }}
        data-testid={`${testidPrefix}-preview`}
      >
        <Avatar src={src} name={name} avatarKey={avatarKey} size={px} shape="rounded" bordered={false} />
        {busy && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center text-white text-xs">
            Working…
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden"
          onChange={(e) => doUpload(e.target.files?.[0])} data-testid={`${testidPrefix}-input`} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          data-testid={`${testidPrefix}-upload-btn`}
          className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-60">
          <Upload size={12} /> {resolved ? "Change photo" : "Upload photo"}
        </button>
        {allowDelete && resolved && (
          <button type="button" onClick={doDelete} disabled={busy}
            data-testid={`${testidPrefix}-delete-btn`}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-red-300 border border-red-500/25 hover:bg-red-500/10 disabled:opacity-60">
            <Trash2 size={12} /> Remove
          </button>
        )}
        <div className="max-w-[190px] text-[10px] leading-relaxed text-white/40">
          JPG / PNG / WEBP · ≤ {MAX_MB} MB<br />Automatically compressed for fast loading
        </div>
      </div>
    </div>
  );
}

/** Shared member avatar used across uploads, profiles, cards, and tables. */
export function Avatar({
  src,
  name,
  avatarKey,
  size = 44,
  shape = "circle",
  bordered = true,
  className = "",
  style = {},
  crossOrigin,
}) {
  const { settings } = useSettings();
  const resolved = resolvePhotoUrl(src);
  const presetUrl = resolvePhotoUrl(
    (settings?.avatar_presets || []).find((preset) => preset.key === avatarKey)?.image_url
  );
  const [failedUrl, setFailedUrl] = useState(null);
  useEffect(() => setFailedUrl(null), [resolved, presetUrl]);

  const radiusClass = shape === "rounded" ? "rounded-2xl" : "rounded-full";
  const borderClass = bordered ? "border border-white/10" : "";
  const avatarStyle = { width: size, height: size, ...style };

  if (resolved && failedUrl !== resolved) {
    return (
      <img
        src={resolved}
        alt={name || "Member"}
        style={avatarStyle}
        className={`${radiusClass} object-cover ${borderClass} ${className}`}
        crossOrigin={crossOrigin}
        onError={() => setFailedUrl(resolved)}
      />
    );
  }
  if (!resolved && AVATAR_KEYS.has(avatarKey) && presetUrl && failedUrl !== presetUrl) {
    return (
      <img
        src={presetUrl}
        alt={`${name || "Member"} selected profile avatar`}
        style={avatarStyle}
        className={`${radiusClass} object-cover ${borderClass} ${className}`}
        onError={() => setFailedUrl(presetUrl)}
      />
    );
  }
  const initials = (name || "").trim().split(/\s+/).map((part) => part[0])
    .filter(Boolean).slice(0, 2).join("").toUpperCase() || "PN";
  const numericSize = typeof size === "number" ? size : 44;
  return (
    <div
      style={{ ...avatarStyle, fontSize: style.fontSize || Math.max(14, Math.round(numericSize * 0.3)) }}
      className={`${radiusClass} bg-gradient-to-br from-violet-500/50 to-magenta-500/40 ${borderClass} flex items-center justify-center text-white font-display font-semibold ${className}`}
      aria-label={`${name || "Member"} profile picture placeholder`}
    >
      {initials}
    </div>
  );
}

export const AVATAR_OPTIONS = [
  { key: "portrait-1", label: "Avatar 1" },
  { key: "portrait-2", label: "Avatar 2" },
  { key: "portrait-3", label: "Avatar 3" },
  { key: "portrait-4", label: "Avatar 4" },
];

const AVATAR_KEYS = new Set(AVATAR_OPTIONS.map(({ key }) => key));
